import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Button, Dropdown, Form, Menu, message, Modal, Select } from 'antd';
import { v1 as uuid } from 'uuid';
import { BasketsTable } from '../components/table';
import { BasketService } from '../services';
import { Basket } from '../types';
import { useTranslation } from 'react-i18next';
import { selectFilterOption } from '@shared/modules/form/utils';
import { SettingsContext } from '@modules/settings';
import { CountryContext } from '@modules/country';

export const BasketSelection: FC<{
  onSubmitSucceed?: () => any;
  onClose?: () => any;
  visible?: boolean;
  baskets: Basket[];
}> = ({ onSubmitSucceed, onClose, visible, baskets }) => {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [selectedBasketId, setSelectedBasketId] = useState<string | null>(null);
  const [country] = useContext(CountryContext);
  const { getCountryId } = useContext(SettingsContext);

  const chooseBasket = useCallback(async () => {
    setSubmitting(true);
    const result = await BasketService.choose(selectedBasketId || 0);

    if (result.status === 200) {
      await onSubmitSucceed?.();
    } else {
      message.error(result.data);
    }
    setSubmitting(false);
  }, [onSubmitSucceed, selectedBasketId]);

  const exportAsExcel = useCallback(
    async (query: { declared?: boolean } = {}) => {
      message.loading({ key: '1', content: t('common.processing') });
      const result = await BasketService.exportAsExcel({ ...query, countryId: getCountryId(country.selected) });

      if (result.status === 200) {
        message.destroy();
        const anchor = document.createElement('a');
        const url = window.URL.createObjectURL(result.data);

        document.body.appendChild(anchor);
        anchor.href = url;
        anchor.download = uuid();
        anchor.click();

        window.URL.revokeObjectURL(url);
      } else {
        message.error({ key: '1', content: result.data });
      }
    },
    [country.selected, t, getCountryId],
  );

  useEffect(() => {
    setSelectedBasketId(null);
  }, [visible]);

  const basketOptions = useMemo(
    () =>
      baskets.map((basket) => (
        <Select.Option key={basket.id} value={basket.id.toString()}>
          {basket.name} - {t('declarations.count')}: {basket.counts.declarations}
        </Select.Option>
      )),
    [baskets, t],
  );

  const footer = useMemo(
    () => [
      <Button onClick={onClose} key='close'>
        {t('common.actions.close')}
      </Button>,
      <Dropdown
        overlay={
          <Menu>
            <Menu.Item onClick={() => exportAsExcel({ declared: true })}>{t('declarations.declared')}</Menu.Item>
            <Menu.Item onClick={() => exportAsExcel({ declared: false })}>{t('declarations.undeclared')}</Menu.Item>
            <Menu.Item onClick={() => exportAsExcel()}>{t('common.all')}</Menu.Item>
          </Menu>
        }
      >
        <Button key='export-as-excel'>{t('common.exportAsExcel')}</Button>
      </Dropdown>,
      <Button loading={submitting} disabled={!selectedBasketId} onClick={chooseBasket} type='primary' key='change'>
        {t('common.actions.update')}
      </Button>,
    ],
    [chooseBasket, exportAsExcel, onClose, selectedBasketId, submitting, t],
  );

  return (
    <Modal width={576} open={visible} onCancel={onClose} title={t('baskets.operations')} footer={footer}>
      <Form>
        <Select
          allowClear
          showSearch
          filterOption={selectFilterOption}
          value={selectedBasketId || undefined}
          onChange={(value: any) => setSelectedBasketId(value || null)}
          placeholder={t('baskets.choose')}
          style={{ display: 'block' }}
        >
          {basketOptions}
        </Select>
      </Form>
      <div style={{ marginTop: 24, marginBottom: -24 }}>
        <BasketsTable dataSource={baskets} />
      </div>
    </Modal>
  );
};
